/**
 * Audience Builder API client.
 * Thin wrappers around the /api/audiences/* endpoints (Phase 1).
 */
import axios from "axios";
import { API } from "@/App";

function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

export async function parseAudienceQuery(token, q) {
  const { data } = await axios.post(`${API}/audiences/parse`, { q }, { headers: authHeaders(token) });
  return data;
}

export async function runAudienceQuery(token, opts = {}) {
  const body = {
    q: opts.q ?? "",
    query: opts.query,
    page: opts.page ?? 1,
    page_size: opts.pageSize ?? 50,
    sort: opts.sort,
    sort_dir: opts.sortDir ?? "desc",
    fields: opts.fields ?? "slim",
  };
  const { data } = await axios.post(`${API}/audiences/query`, body, { headers: authHeaders(token) });
  return data;
}

export async function fetchAudienceFacets(token, opts = {}) {
  const body = {
    q: opts.q ?? "",
    query: opts.query,
    fields: opts.fields,
    limit_per_field: opts.limitPerField ?? 20,
  };
  const { data } = await axios.post(`${API}/audiences/facets`, body, { headers: authHeaders(token) });
  return data;
}

export async function listAudiences(token) {
  const { data } = await axios.get(`${API}/audiences`, { headers: authHeaders(token) });
  return data;
}

export async function createAudience(token, body) {
  const { data } = await axios.post(`${API}/audiences`, body, { headers: authHeaders(token) });
  return data;
}

export async function getAudience(token, id) {
  const { data } = await axios.get(`${API}/audiences/${id}`, { headers: authHeaders(token) });
  return data;
}

export async function patchAudience(token, id, body) {
  const { data } = await axios.patch(`${API}/audiences/${id}`, body, { headers: authHeaders(token) });
  return data;
}

export async function deleteAudience(token, id) {
  await axios.delete(`${API}/audiences/${id}`, { headers: authHeaders(token) });
}

export async function rerunAudience(token, id) {
  const { data } = await axios.post(`${API}/audiences/${id}/run`, {}, { headers: authHeaders(token) });
  return data;
}

export async function cloneAudience(token, id) {
  const { data } = await axios.post(`${API}/audiences/${id}/clone`, {}, { headers: authHeaders(token) });
  return data;
}

export async function aiParseAudience(token, opts = {}) {
  const body = { prompt: opts.prompt };
  if (opts.sampleFieldValues) body.sample_field_values = opts.sampleFieldValues;
  const { data } = await axios.post(`${API}/audiences/ai-parse`, body, {
    headers: authHeaders(token),
  });
  return data;
}

/**
 * Phase-3 — Trigger a CSV/JSONL export download in the browser.
 * Returns a blob URL that the caller is responsible for revoking.
 */
export async function exportAudience(token, id, opts = {}) {
  const params = new URLSearchParams({
    format: opts.format || "csv",
    scope: opts.scope || "live",
    limit: String(opts.limit ?? 5000),
  });
  if (opts.fields && opts.fields.length) params.set("fields", opts.fields.join(","));
  const { data, headers } = await axios.get(
    `${API}/audiences/${id}/export?${params}`,
    { headers: authHeaders(token), responseType: "blob" },
  );
  const cd = headers["content-disposition"] || "";
  const m = cd.match(/filename="([^"]+)"/);
  const filename = m ? m[1] : `audience-${id}.${opts.format || "csv"}`;
  const url = URL.createObjectURL(data);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { filename, rowCount: parseInt(headers["x-audience-row-count"] || "0", 10) };
}

export async function handoffAudience(token, id) {
  const { data } = await axios.post(`${API}/audiences/${id}/handoff`, {}, {
    headers: authHeaders(token),
  });
  return data;
}

/**
 * Build the deep-link URL into the Leads page with this audience pre-applied.
 * The Leads page reads `?aid=...` (saved audience) or `?aq=...` (raw query string).
 */
export function buildLeadsDeeplink({ audienceId, queryString }) {
  const params = new URLSearchParams();
  if (audienceId) params.set("aid", audienceId);
  if (queryString) params.set("aq", queryString);
  return `/leads?${params.toString()}`;
}
